import { Component, OnInit, Output, EventEmitter, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { take } from 'rxjs/operators';

import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { ContractsV4Service } from '../../../../zentility/_shared/_services/v4/contracts-v4.service';
import { CustomerPlanFinderEntry, EnergyPlanNew, ZenContractV4ListModel, ZenContractDialogDataModel, NearestRankedEnergyPlanModel } from '../../../../zentility/_shared/_model/contract-v4.model';
import { RateType } from '../../../../zentility/_shared/_zen-legacy-common/_models/energyplan-v2';
import { CommodityType } from '../../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { ZenEditContractDialogComponent } from '../../../../zentility/_shared/_dialogs/zen-edit-contract-dialog/zen-edit-contract-dialog.component';
import { ButtonTypes, ZenDialogDataModel, ZenDialogDataType } from '../../../../zentility/_shared/_dialogs/zen-dialog/zen-dialog.component';
import { ZenDialogSizeEnum, ZenDialogPanelClassEnum } from '../../../../zentility/_shared/_enums/zen-dialogs.enum';
import { BillingMethod } from '../../../../zentility/_shared/_model/product-configuration.model';
import { ScoreSelectComponent, ScoreSelectOption } from '../score-select/score-select.component';
import { SharedModule } from '../../../../zentility/_shared/shared.module';
import { formatBillPeriod } from '../../../utils/date-format.util';
import { buildPlanSubtitle } from '../../../utils/plan-format.util';
import { resolveGasUnitId } from '../../../services/bill-persistence.service';

interface SupplierBillRow {
  billGroupId: string;
  meterGroupId: string;
  meterUan: string;
  saGroupId: string;
  saState: string;
  billPeriod: string;
  supplierName: string;
  supplierId: string;
  confidence: number;
  energyPlanId?: number;
  commodityType: CommodityType;
  utilityId?: number;
  // EP selection state
  energyPlanOptions: ScoreSelectOption[];
  selectedEnergyPlanId?: number;
  loading: boolean;
}

@Component({
  selector: 'app-bill-ai-flow-energy-plan-prompt',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatSelectModule, MatFormFieldModule, ScoreSelectComponent, SharedModule],
  templateUrl: './bill-ai-flow-energy-plan-prompt.component.html',
  styleUrl: './bill-ai-flow-energy-plan-prompt.component.scss'
})
export class BillAiFlowEnergyPlanPromptComponent implements OnInit {
  @Output() back = new EventEmitter<void>();

  phase = signal<'prompt' | 'select' | 'creating-drafts'>('prompt');
  supplierBillRows: SupplierBillRow[] = [];
  isCreatingDrafts = false;
  draftError: string | null = null;

  readonly state = inject(BillAiFlowStore);
  private contractsV4Service = inject(ContractsV4Service);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.buildSupplierBillRows();
  }

  private buildSupplierBillRows(): void {
    const response = this.state.matchResponse();
    const billData = this.state.billData();
    const matchRequest = this.state.matchRequest();
    const skipped = this.state.skippedBillGroupIds();
    if (!response || !matchRequest) return;

    for (const len of response.legalEntities) {
      for (const sa of len.serviceAddresses) {
        // Resolve the SA's state from edits first (the user may have changed
        // the state or moved a meter into a different-state SA), falling back
        // to the original match request when the user hasn't touched it.
        const saRequest = matchRequest.legalEntities
          .find(l => l.groupId === len.groupId)?.serviceAddresses
          .find(s => s.groupId === sa.groupId);
        const saState = this.state.getSaEdit(sa.groupId)?.state || saRequest?.state || '';

        for (const meter of sa.meters) {
          for (const bill of meter.bills) {
            if (bill.supplier?.supplierId == null) continue; // skip utility-only
            if (skipped.has(bill.groupId)) continue; // skipped duplicate: not imported

            const data = billData[bill.groupId];
            if (!data) continue;

            const meterRequest = saRequest?.meters.find(m => m.groupId === meter.groupId);

            this.supplierBillRows.push({
              billGroupId: bill.groupId,
              meterGroupId: meter.groupId,
              meterUan: data.utilityAccountNumber || meter.groupId,
              saGroupId: sa.groupId,
              saState,
              billPeriod: formatBillPeriod(data.billingPeriodStart, data.billingPeriodEnd),
              supplierName: bill.supplier.supplierName,
              supplierId: bill.supplier.supplierId!,
              confidence: bill.supplier.confidence,
              energyPlanId: bill.supplier.energyPlanId,
              commodityType: meterRequest?.commodityType || CommodityType.Electricity,
              utilityId: meter.utility?.utilityId || undefined,
              energyPlanOptions: [],
              selectedEnergyPlanId: bill.supplier.energyPlanId,
              loading: false,
            });
          }
        }
      }
    }
  }

  chooseSetNow(): void {
    this.phase.set('select');
    this.loadAllPlans();
  }

  async chooseImportDraft(): Promise<void> {
    this.phase.set('creating-drafts');
    this.isCreatingDrafts = true;
    this.draftError = null;

    try {
      const customerId = this.getCustomerId();
      if (!customerId) {
        this.draftError = 'Customer must be selected before creating draft plans.';
        this.isCreatingDrafts = false;
        return;
      }

      // Create draft energy plans for rows that don't already have one.
      // Per-row failures are aggregated so the user sees how many drafts
      // actually got created, instead of being silently dropped to the
      // summary step on a partial failure.
      const failedRows: string[] = [];
      for (const row of this.supplierBillRows) {
        if (row.energyPlanId) {
          this.applyEnergyPlanToBill(row.billGroupId, row.energyPlanId, row.supplierId, row.supplierName);
          continue;
        }

        const data = this.state.getBillData(row.billGroupId);
        const isGas = row.commodityType === CommodityType.Gas;
        const unitId = isGas ? resolveGasUnitId(data?.usageUnitId, data?.usageUnit) : undefined;
        // The rate is per the bill's usage unit; a gas rate in an unknown unit would be stored as Dth
        const supplierRate = (isGas && unitId === undefined) ? 0 : this.getBillSupplierRate(row.billGroupId) ?? 0;

        const planData: EnergyPlanNew = {
          supplierId: row.supplierId,
          commodityType: row.commodityType as any,
          state: row.saState,
          utilityIds: row.utilityId ? [row.utilityId] : [],
          supplierRate,
          unitId,
          rateType: RateType.fixed,
          draftContract: true,
        };

        try {
          const result = await this.contractsV4Service
            .createContracts(customerId, planData)
            .pipe(take(1))
            .toPromise();

          if (result?.energyPlanId) {
            this.applyEnergyPlanToBill(row.billGroupId, result.energyPlanId, row.supplierId, row.supplierName);
          } else {
            failedRows.push(row.supplierName);
            this.applyEnergyPlanToBill(row.billGroupId, undefined, row.supplierId, row.supplierName);
          }
        } catch (err: any) {
          console.warn(`Failed to create draft EP for bill ${row.billGroupId}:`, err);
          failedRows.push(row.supplierName);
          this.applyEnergyPlanToBill(row.billGroupId, undefined, row.supplierId, row.supplierName);
        }
      }

      if (failedRows.length > 0) {
        const unique = Array.from(new Set(failedRows));
        this.draftError = `Couldn't create draft plans for ${failedRows.length} bill(s) (${unique.join(', ')}). You can finish these from contract management.`;
        this.isCreatingDrafts = false;
        return;
      }

      this.state.setFastTrackEnergyPlanChoice('later');
      this.state.completeStep('bill-review');
      this.state.goToStep('summary');
    } catch (err: any) {
      this.draftError = err?.message || 'Failed to create draft energy plans.';
    } finally {
      this.isCreatingDrafts = false;
    }
  }

  continueWithPlans(): void {
    // Apply selected energy plans to bill data
    for (const row of this.supplierBillRows) {
      if (row.selectedEnergyPlanId) {
        this.applyEnergyPlanToBill(row.billGroupId, row.selectedEnergyPlanId, row.supplierId, row.supplierName);
      }
    }

    this.state.setFastTrackEnergyPlanChoice('now');
    this.state.completeStep('bill-review');
    this.state.goToStep('summary');
  }

  onEnergyPlanChange(row: SupplierBillRow, planId: number): void {
    row.selectedEnergyPlanId = planId;

    // Auto-propagate to sibling rows with matching commodity, supplier and rate that
    // haven't been manually set yet.
    const selectedOption = row.energyPlanOptions.find(o => o.value === planId);
    if (selectedOption) {
      this.propagatePlanToSiblings(row, planId, selectedOption);
    }

    this.updateRowState();
  }

  openCreatePlanDialog(row: SupplierBillRow): void {
    const customerId = this.getCustomerId();
    if (!customerId) {
      console.error('Cannot open plan dialog: no customer ID available');
      return;
    }

    const commodityType = row.commodityType === CommodityType.Gas ? CommodityType.Gas : CommodityType.Electricity;
    const serviceName = commodityType === CommodityType.Electricity ? 'Electricity' : 'Gas';

    // Compute approximate supply rate from the bill
    const data = this.state.getBillData(row.billGroupId);
    const usage = data?.usageAmount || 0;
    const supplyCharges = data?.supplyCharges || 0;
    const distributionCharges = data?.distributionCharges || 0;
    const hasSupply = supplyCharges > 0;
    const hasDist = distributionCharges > 0;
    const billingMethod = (hasSupply && hasDist) ? BillingMethod.consolidated : BillingMethod.dual;
    const rateUnitId = commodityType === CommodityType.Gas ? resolveGasUnitId(data?.usageUnitId, data?.usageUnit) : undefined;
    // A gas rate in an unknown unit would be saved as Dth, so it is only filled in with its unit
    const supplyRate = (usage > 0 && hasSupply && (commodityType !== CommodityType.Gas || rateUnitId !== undefined))
      ? parseFloat((supplyCharges / usage).toFixed(5))
      : undefined;

    let dialogRef: any;
    const dialogData: ZenDialogDataModel = {
      type: ZenDialogDataType.ADD_DIALOG,
      header: { title: `Add ${serviceName} Contract` },
      bodyText: `Add the required details below to create a new ${serviceName} supply contract.`,
      data: {
        lockedCustomerId: customerId,
        lockedCommodityType: commodityType,
        lockedState: row.saState || '',
        lockedSupplierId: row.supplierId || undefined,
        lockedSupplierRate: supplyRate,
        lockedSupplierRateUnitId: rateUnitId,
        lockedBillingMethod: billingMethod,
      } as Partial<ZenContractDialogDataModel>,
      onClose: () => dialogRef.close(),
      actionButtons: [
        { label: 'Cancel', command: () => dialogRef.close() },
        {
          label: 'Save', btnType: ButtonTypes.MAT_RAISED_BUTTON, color: 'primary', styleClass: 'ms-5',
          command: () => dialogRef.close()
        }
      ]
    };

    dialogRef = this.dialog.open(ZenEditContractDialogComponent, {
      width: ZenDialogSizeEnum.LARGE,
      panelClass: ZenDialogPanelClassEnum.MOBILE_FULL_HEIGHT,
      data: dialogData,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: ZenContractV4ListModel) => {
      if (result) {
        const isDraft = result.planEndDate == null;

        // User explicitly created this plan — high confidence
        const newOption: ScoreSelectOption = {
          value: result.energyPlanId,
          label: this.buildPlanLabel(result),
          subtitle: buildPlanSubtitle(result),
          score: 95,
          scoreClass: 'high',
          badge: isDraft ? 'DRAFT' : (result.csa ? 'CSA' : undefined),
        };

        // Inject into THIS row and auto-select
        if (!row.energyPlanOptions.some(o => o.value === result.energyPlanId)) {
          row.energyPlanOptions = [newOption, ...row.energyPlanOptions];
        }
        row.selectedEnergyPlanId = result.energyPlanId;
        row.supplierId = result.supplierId;
        row.supplierName = result.supplierName;

        // Propagate to OTHER rows that match the same supplier + state + commodity
        for (const otherRow of this.supplierBillRows) {
          if (otherRow.billGroupId === row.billGroupId) continue;
          if (otherRow.commodityType !== row.commodityType) continue;
          if (otherRow.supplierId !== result.supplierId) continue;
          if (!isDraft && otherRow.saState !== row.saState) continue;

          const otherOption: ScoreSelectOption = {
            value: result.energyPlanId,
            label: newOption.label,
            score: 90,
            scoreClass: 'high',
            badge: newOption.badge,
          };

          if (!otherRow.energyPlanOptions.some(o => o.value === result.energyPlanId)) {
            otherRow.energyPlanOptions = [otherOption, ...otherRow.energyPlanOptions];
          }

          // Auto-select if the row doesn't have a plan yet
          if (!otherRow.selectedEnergyPlanId) {
            otherRow.selectedEnergyPlanId = result.energyPlanId;
          }
        }
        this.updateRowState();
      }
    });
  }

  allPlansSelected = false;
  anyLoading = false;

  private updateRowState(): void {
    this.allPlansSelected = this.supplierBillRows.every(r => r.selectedEnergyPlanId != null);
    this.anyLoading = this.supplierBillRows.some(r => r.loading);
  }

  goBack(): void {
    if (this.phase() === 'select') {
      // Go back to prompt phase, not the store's previous step
      this.phase.set('prompt');
    } else {
      // From prompt phase, tell parent to go back to overview
      this.back.emit();
    }
  }

  private getCustomerId(): number | null {
    const context = this.state.getContext();
    if (context?.customerId) {
      return parseInt(context.customerId, 10);
    }
    const selected = this.state.selectedCustomerId();
    if (selected == null) return null;
    const parsed = typeof selected === 'number' ? selected : parseInt(String(selected), 10);
    return isNaN(parsed) ? null : parsed;
  }

  private applyEnergyPlanToBill(billGroupId: string, energyPlanId: number | undefined, supplierId: string, supplierName: string): void {
    const data = this.state.getBillData(billGroupId);
    if (!data) return;

    this.state.updateBillData(billGroupId, {
      ...data,
      supplierId,
      providerName: supplierName,
      energyPlanId,
    });
  }

  private loadAllPlans(): void {
    const customerId = this.getCustomerId();
    if (!customerId) return;

    const selections = this.state.entitySelections();
    const entries: CustomerPlanFinderEntry[] = [];

    for (const row of this.supplierBillRows) {
      const data = this.state.getBillData(row.billGroupId);

      // Skip bills with missing dates — backend requires non-null LocalDate
      if (!data?.billingPeriodStart || !data?.billingPeriodEnd) {
        row.loading = false;
        row.energyPlanOptions = [];
        continue;
      }

      row.loading = true;
      const meterId = Number(selections[row.meterGroupId]) || 0;
      const saId = Number(selections[row.saGroupId]) || 0;

      const supplierRate = this.getBillSupplierRate(row.billGroupId);

      entries.push({
        key: row.billGroupId,
        supplierId: row.supplierId,
        state: row.saState,
        commodityType: row.commodityType,
        billStartDate: data.billingPeriodStart,
        billEndDate: data.billingPeriodEnd,
        meterId,
        serviceAddressId: saId,
        supplierRate,
      });
    }
    this.updateRowState();

    this.contractsV4Service.findEnergyPlansForCustomer(customerId, entries)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          for (const row of this.supplierBillRows) {
            row.loading = false;
            const result = response.results?.[row.billGroupId];
            if (result) {
              const plans = result.energyPlans || [];
              row.energyPlanOptions = this.buildScoreOptions(plans, row);
              this.autoSelectPlan(row, plans);
            } else {
              row.energyPlanOptions = [];
            }
          }
          // Propagate auto-selected plans to sibling rows with same commodity, supplier and rate
          this.propagateSelectionsToSiblings();
          this.updateRowState();
        },
        error: (err) => {
          console.error('Failed to load energy plans:', err);
          for (const row of this.supplierBillRows) {
            row.loading = false;
            row.energyPlanOptions = [];
          }
          this.updateRowState();
        }
      });
  }

  /**
   * After initial auto-select, propagate selected plans to sibling rows
   * (same commodity, supplier and rate) that don't yet have a selection.
   */
  private propagateSelectionsToSiblings(): void {
    const selectedRows = this.supplierBillRows.filter(r => r.selectedEnergyPlanId != null);
    for (const source of selectedRows) {
      const option = source.energyPlanOptions.find(o => o.value === source.selectedEnergyPlanId);
      if (!option) continue;
      this.propagatePlanToSiblings(source, source.selectedEnergyPlanId, option);
    }
  }

  /**
   * Propagate a plan selection to sibling rows with matching commodity, supplier and rate.
   * Boosts score to 100 since another meter in this batch already matched.
   */
  private propagatePlanToSiblings(source: SupplierBillRow, planId: number, option: ScoreSelectOption): void {
    const sourceRate = this.getBillSupplierRate(source.billGroupId);
    for (const sibling of this.supplierBillRows) {
      if (sibling.billGroupId === source.billGroupId) continue;
      if (sibling.commodityType !== source.commodityType) continue;
      if (sibling.supplierId !== source.supplierId) continue;
      if (sibling.selectedEnergyPlanId) continue;

      const siblingRate = this.getBillSupplierRate(sibling.billGroupId);
      if (!this.ratesMatch(sourceRate, siblingRate)) continue;

      const existing = sibling.energyPlanOptions.find(o => o.value === planId);
      if (existing) {
        existing.score = 100;
        existing.scoreClass = 'high';
      } else {
        sibling.energyPlanOptions = [{ ...option, score: 100, scoreClass: 'high' }, ...sibling.energyPlanOptions];
      }
      sibling.selectedEnergyPlanId = planId;
    }
  }

  private static readonly AUTO_SELECT_MIN_SCORE = 90;

  private autoSelectPlan(row: SupplierBillRow, plans: any[]): void {
    // If the match response already assigned a specific EP, use it
    if (row.energyPlanId && plans.some((p: any) => p.energyPlanId === row.energyPlanId)) {
      row.selectedEnergyPlanId = row.energyPlanId;
      return;
    }

    // Auto-select if rate matches exactly OR rank is very high (90+, e.g. meter-linked overlap)
    const billRate = this.getBillSupplierRate(row.billGroupId);
    const candidates = plans.filter((p: any) => {
      const hasRateMatch = this.ratesMatch(billRate, p.supplierRate != null ? p.supplierRate : undefined);
      const hasHighRank = (p.rank ?? 0) >= BillAiFlowEnergyPlanPromptComponent.AUTO_SELECT_MIN_SCORE;
      return hasRateMatch || hasHighRank;
    });

    if (candidates.length > 0) {
      // Pick the highest-ranked among eligible plans
      const candidateIds = new Set(candidates.map((p: any) => p.energyPlanId));
      const eligible = row.energyPlanOptions.filter(o => candidateIds.has(o.value));
      if (eligible.length > 0) {
        const best = eligible.reduce((a, b) => (b.score ?? 0) > (a.score ?? 0) ? b : a);
        row.selectedEnergyPlanId = best.value;
      }
    }
    // If no rate match and no high-rank plan, don't auto-select — user must choose manually
  }

  /**
   * Build dropdown options from plans returned by the backend.
   * The backend plan-finder API handles all ranking (meter linkage, dates,
   * expiration). The frontend just converts to display options — no re-scoring.
   */
  private buildScoreOptions(plans: any[], row: SupplierBillRow): ScoreSelectOption[] {
    return plans.map((p: any) => {
      const isDraft = p.planEndDate == null;
      // Use backend rank directly — it already accounts for meter linkage,
      // date overlap, expiration, and plan hierarchy. No rank means no chip;
      // 0% would be misleading.
      const score = p.rank;

      return {
        value: p.energyPlanId,
        label: this.buildPlanLabel(p),
        subtitle: buildPlanSubtitle(p),
        score: score ?? undefined,
        scoreClass: score == null ? undefined : score >= 80 ? 'high' : score > 50 ? 'med' : 'low',
        badge: isDraft ? 'DRAFT' : (p.csa ? 'CSA' : undefined),
      };
    });
  }

  /** Compute per-unit supply rate for a bill. */
  private getBillSupplierRate(billGroupId: string): number | undefined {
    const data = this.state.getBillData(billGroupId);
    if (!data) return undefined;
    const usage = data.usageAmount || 0;
    const supply = data.supplyCharges || 0;
    return (usage > 0 && supply > 0)
      ? parseFloat((supply / usage).toFixed(5))
      : undefined;
  }

  /** True if two rates are identical (within rounding tolerance). */
  private ratesMatch(a: number | undefined, b: number | undefined): boolean {
    if (a == null || b == null) return false;
    return Math.abs(a - b) < 0.00005;
  }

  private buildPlanLabel(plan: any): string {
    const isDraft = plan.planEndDate == null;
    // Show the plan's own rate unit after the rate, e.g. "@ 0.07500 / kWh" —
    // matches the bill-form energy-plan dropdown (DEV-15108).
    const unitSuffix = plan.unit ? ` / ${plan.unit}` : '';
    const rate = plan.supplierRate != null ? `${Number(plan.supplierRate).toFixed(5)}${unitSuffix}` : '';
    if (isDraft) {
      return rate ? `${plan.supplierName} @ ${rate} (DRAFT)` : `${plan.supplierName} (DRAFT)`;
    }
    const term = plan.termMonths != null ? `${plan.termMonths}` : '?';
    return rate ? `${plan.supplierName} for ${term} Mo @ ${rate}` : `${plan.supplierName} for ${term} Mo`;
  }

}
